"use client";

import Link from "next/link";
import { CheckCircle2, Smartphone, Bell, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

export default function MobileProductPage() {
  const { t } = useI18n();

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-block bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-6">
            <span className="text-amber-700 font-semibold">
              {t("pages.product.mobile.badge") || "Coming Soon"}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            {t("pages.product.mobile.headline") ||
              "Treasury в кармане. iOS & Android."}
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            {t("pages.product.mobile.description") ||
              `Утверждайте платежи с телефона. Получайте push-уведомления.
              Проверяйте KPI. Всё что нужно казначею или GM – в мобильном приложении.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="outline" className="h-12">
              <Link href="/contact">
                {t("pages.product.mobile.waitlist") || "Записаться в очередь"}
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12">
              <Link href="/pricing">
                {t("pages.product.mobile.pricing") || "Смотреть тарифы"}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.mobile.timelineTitle") || "Что ждёт вас"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-3">
              {t("pages.product.mobile.phase1.title") || "Q2 2026 — iOS (Private Beta)"}
            </h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.phase1.desc") ||
                "Первый выпуск на iOS с основным функционалом."}
            </p>
            <ul className="space-y-2">
              {[
                "Утверждение платежей",
                "KPI Dashboard (mini)",
                "Push-уведомления",
                "Offline mode (limited)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 size={16} className="text-keiro-green flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-3">
              {t("pages.product.mobile.phase2.title") || "Q3 2026 — Android + iOS Public"}
            </h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.phase2.desc") ||
                "Выпуск на Android и публичный релиз iOS."}
            </p>
            <ul className="space-y-2">
              {[
                "Полный Treasury функционал",
                "Advanced Analytics",
                "Biometric auth",
                "Offline sync",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 size={16} className="text-keiro-green flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.mobile.featuresTitle") || "Основные возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: "✅",
              titleKey: "pages.product.mobile.features.approvals.title",
              descKey: "pages.product.mobile.features.approvals.desc",
            },
            {
              icon: "📱",
              titleKey: "pages.product.mobile.features.interface.title",
              descKey: "pages.product.mobile.features.interface.desc",
            },
            {
              icon: "🔔",
              titleKey: "pages.product.mobile.features.notifications.title",
              descKey: "pages.product.mobile.features.notifications.desc",
            },
            {
              icon: "📊",
              titleKey: "pages.product.mobile.features.kpi.title",
              descKey: "pages.product.mobile.features.kpi.desc",
            },
            {
              icon: "🔐",
              titleKey: "pages.product.mobile.features.security.title",
              descKey: "pages.product.mobile.features.security.desc",
            },
            {
              icon: "⚡",
              titleKey: "pages.product.mobile.features.offline.title",
              descKey: "pages.product.mobile.features.offline.desc",
            },
          ].map((feature, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="text-3xl mb-3">{feature.icon}</div>
              <h3 className="font-bold text-slate-900 mb-2">
                {t(feature.titleKey) || feature.titleKey}
              </h3>
              <p className="text-slate-600">
                {t(feature.descKey) || feature.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Treasury on Mobile */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.mobile.treasuryTitle") ||
                "Утверждайте платежи одним касанием"}
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              {t("pages.product.mobile.treasuryDesc") ||
                `Письмо от поставщика пришло на телефон?
                Заявка на платёж уже в Keiront?
                Утвердить можно за 10 секунд.
                Даже в аэропорту, даже в отпуске.`}
            </p>
            <ul className="space-y-3">
              {[
                "Список всех платежей в очереди",
                "Быстрый просмотр суммы и цели",
                "Одобрение или отклонение со статусом",
                "История решений в приложении",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Smartphone size={20} className="text-keiro-green flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center h-96 flex items-center justify-center">
            <p className="text-slate-600">
              {t("pages.product.mobile.mockup") || "Макет интерфейса iOS"}
            </p>
          </div>
        </div>
      </section>

      {/* Push Notifications */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center h-96 flex items-center justify-center order-2 lg:order-1">
            <p className="text-slate-600">
              {t("pages.product.mobile.notificationMockup") ||
                "Пример push-уведомления"}
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.mobile.notificationTitle") ||
                "Push-уведомления в реальном времени"}
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              {t("pages.product.mobile.notificationDesc") ||
                `Никогда не пропустите важное.
                Новая заявка на оплату? Уведомление.
                Срок платежа завтра? Напоминание.
                Аномалия в расходах? Алерт.`}
            </p>
            <ul className="space-y-3">
              {[
                "Критические события – мгновенно",
                "Плановые события – по расписанию",
                "Брэнд-специфичные уведомления",
                "Управление в приложении (вкл/выкл)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Bell size={20} className="text-keiro-green flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* KPI on Mobile */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.mobile.kpiTitle") ||
            "KPI в кармане"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border border-slate-200 rounded-xl p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              {t("pages.product.mobile.kpiCard1.title") ||
                "Dashboard-on-the-go"}
            </h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.kpiCard1.desc") ||
                "Быстрый вид на RevPAR, OCC, GOPPAR."}
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              {[
                "Top 3 метрики по выбору",
                "Sparklines для трендов",
                "Сравнение с вчера и годом ранее",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-keiro-green rounded-full" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-slate-200 rounded-xl p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              {t("pages.product.mobile.kpiCard2.title") ||
                "Аномалии в уведомлениях"}
            </h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.kpiCard2.desc") ||
                "AI выявляет скачки и шлёт алерты."}
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              {[
                "RevPAR упал на 20%? Уведомление",
                "OCC ниже плана 3 дня подряд? Уведомление",
                "Действуйте, не открывая приложение",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-keiro-green rounded-full" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.mobile.securityTitle") ||
            "Безопасность на первом месте"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.mobile.security.biometric.title") ||
                "Биометрия",
              desc: t("pages.product.mobile.security.biometric.desc") ||
                "Отпечаток пальца или Face ID",
            },
            {
              title: t("pages.product.mobile.security.encryption.title") ||
                "Шифрование",
              desc: t("pages.product.mobile.security.encryption.desc") ||
                "TLS 1.3 + локальное шифрование",
            },
            {
              title: t("pages.product.mobile.security.session.title") ||
                "Управление сессией",
              desc: t("pages.product.mobile.security.session.desc") ||
                "Автовыход после 15 минут неактивности",
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <Lock size={28} className="text-keiro-green mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.mobile.useCasesTitle") ||
            "Для кого это полезно"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.mobile.useCase1.title") ||
                "GM / Assistant",
              desc: t("pages.product.mobile.useCase1.desc") ||
                "Утверждайте платежи без ноутбука. Проверяйте KPI одним касанием.",
              emoji: "👔",
            },
            {
              title: t("pages.product.mobile.useCase2.title") ||
                "CFO / Treasurer",
              desc: t("pages.product.mobile.useCase2.desc") ||
                "Контролируйте cash position в любой момент. Быстрые решения на дороге.",
              emoji: "💼",
            },
            {
              title: t("pages.product.mobile.useCase3.title") ||
                "Owner / Investor",
              desc: t("pages.product.mobile.useCase3.desc") ||
                "Следите за производительностью отелей. Главные метрики всегда в кармане.",
              emoji: "📈",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{useCase.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-3">{useCase.title}</h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OS Support */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.mobile.osTitle") || "Поддерживаемые платформы"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border border-slate-200 rounded-xl p-8 text-center">
            <div className="text-5xl mb-4">🍎</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">iOS</h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.ios.version") || "iOS 14+"}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {t("pages.product.mobile.ios.status") ||
                "Private Beta — Q2 2026"}
            </p>
          </div>
          <div className="border border-slate-200 rounded-xl p-8 text-center">
            <div className="text-5xl mb-4">🤖</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Android</h3>
            <p className="text-slate-600 mb-4">
              {t("pages.product.mobile.android.version") || "Android 10+"}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {t("pages.product.mobile.android.status") ||
                "Coming — Q3 2026"}
            </p>
          </div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.mobile.ctaTitle") ||
              "Будьте первыми"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.mobile.ctaDesc") ||
              "Запишитесь в бету. Первые 50 участников получат год скидку 20%."}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.mobile.ctaButton") ||
                "Записаться в очередь"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
